import {
  Table as MuiTable,
  TableBody,
  TableRow,
  Pagination as MuiPagination,
} from '@mui/material';

import {
  HeaderCellStyled,
  TableCellStyled,
  TableHeadStyled,
  Pagination,
  PaginationItemStyled,
} from './styled';

import { useTable } from 'react-table';
import { ChangeEvent, useState } from 'react';
import { API_URL } from '../constants/env';

interface TableProps {
  columns: any;
  data: any;
  metadata: {
    count: number;
    links?: {
      previous?: string;
      next?: string;
    };
    number_pages: number;
    per_page: number;
  };
  perPage?: number;
  onPageChange: (cursor: string) => void;
}

function Table({
  columns,
  data,
  metadata,
  perPage = 6,
  onPageChange,
}: TableProps) {
  const [page, setPage] = useState<number>(1);
  const [serverSidePagination] = useState<boolean>(!!metadata.links);

  let tableData = data;

  // No server side pagination
  if (!serverSidePagination) {
    tableData =
      -1 < page
        ? tableData.slice((page - 1) * perPage, (page - 1) * perPage + perPage)
        : tableData;
  }

  const { headers, rows, prepareRow } = useTable({
    columns,
    data: tableData,
  });

  return (
    <>
      <MuiTable>
        <TableHeadStyled>
          <TableRow>
            {headers.map((column: any) => (
              <HeaderCellStyled {...column.getHeaderProps()}>
                {column.render('Header')}
              </HeaderCellStyled>
            ))}
          </TableRow>
        </TableHeadStyled>
        <TableBody>
          {rows.map((row: any, i: number) => {
            prepareRow(row);
            const { id } = row.original;
            return (
              <TableRow {...row.getRowProps()}>
                {row.cells.map((cell: any) => {
                  const { key, role } = cell.getCellProps();
                  return (
                    <TableCellStyled
                      role={role}
                      key={`${key}-${id}`}
                      data-type={id}
                    >
                      {cell.render('Cell')}
                    </TableCellStyled>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </MuiTable>
      {metadata.count > perPage && (
        <Pagination>
          <MuiPagination
            page={page}
            count={metadata.number_pages}
            shape='rounded'
            onChange={handleChangePage}
            renderItem={item => {
              // Only render 'Prev' and 'Next' items.
              if (item.type === 'next' || item.type === 'previous') {
                let disabled = metadata?.links
                  ? !metadata.links[item.type]
                  : item.disabled;

                if (!serverSidePagination) {
                  disabled = false;

                  switch (item.type) {
                    case 'next':
                      if (page === metadata.number_pages) {
                        disabled = true;
                      }
                      break;
                    case 'previous':
                      if (page === 1) {
                        disabled = true;
                      }
                      break;
                  }
                }

                if (perPage) {
                  if (
                    item.type === 'next' &&
                    page * perPage + 1 > metadata.count
                  ) {
                    disabled = true;
                  }
                }

                return <PaginationItemStyled {...item} disabled={disabled} />;
              }
            }}
          />
          {/*<PaginationInfo>
          Page {page} of {metadata.number_pages}
        </PaginationInfo>*/}
        </Pagination>
      )}
    </>
  );

  function handleChangePage(_: ChangeEvent<unknown>, newPage: number) {
    setPage(newPage);

    if (onPageChange && metadata.links) {
      const newPageUri =
        newPage > page ? metadata.links['next'] : metadata.links['previous'];

      if (newPageUri) {
        const cursorUrl = new URL(`${API_URL}/${newPageUri}`);
        const cursor = cursorUrl.searchParams.get('cursor') || '';

        onPageChange(cursor);
      }
    }
  }
}

export { Table };
